import { Spacer } from "@/components/Spacer";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function Section09({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="w-full flex flex-col items-center bg-[#FFFFFF] desktop:py-[100px] desktop:px-[40px] py-[60px] px-[20px]">
      <div className="desktop:w-[960px] w-[342px] flex flex-col items-center">
        <div
          className="font-eng-01-bold text-[#001122] desktop:text-[32px] text-[24px] leading-[1.2em]"
          style={{ letterSpacing: "-0.02px" }}
        >
          FAQ
        </div>

        <Spacer height={isMobile ? 40 : 60} />
        <div className="w-full flex flex-col items-center gap-[12px]">
          <Question
            title="What is Healthier?"
            description={`Healthier is your go-to AI health partner when you're concerned about your health. It is an AI-powered health management app that provides personalized health insights and disease predictions based on your symptoms, health data, and lifestyle habits.

### Who is it for?
- Those who want to understand their symptoms before visiting a doctor
- Those looking for personalized health guidance but unsure where to start
- Busy professionals who want to stay on top of their health`}
            isMobile={isMobile}
          />
          <Question
            title="How are the health analysis results generated?"
            description={`The AI analyzes your responses to symptom questionnaires along with your health data to generate results.



While the app is based on medical data, it does not provide official diagnoses or prescriptions. 

Please consult a medical professional if your symptoms persist or worsen.`}
            isMobile={isMobile}
          />
          <Question
            title="Is my health information stored securely?"
            description={`Yes. Healthier protects your data with AES-256 encryption and TLS-secured communication.


All personal health data is stored and managed in full compliance with Korean privacy and healthcare information laws.

Access controls and real-time monitoring systems are also in place to prevent unauthorized access.`}
            isMobile={isMobile}
          />
          <Question
            title="Does it cost anything to use Healthier?"
            description={`**Healthier is free to use.**

Core features such as AI symptom analysis and hospital navigation are provided at no cost so that anyone can access health support without financial burden.Feel free to use it anytime your health needs attention.`}
            isMobile={isMobile}
          />
          <Question
            title="What are the customer service hours?"
            description={`Customer support is available Monday to Friday,
              
10 AM to 6 PM (KST).You can reach us via the Healthier KakaoTalk channel,
              
1:1 inquiry board within the app,  or email at be@healthier.kr


We'll get back to you as quickly as possible.`}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
}

const Question = ({
  title,
  description,
  isMobile,
}: {
  title: string;
  description?: string;
  isMobile: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const springTransition = {
    type: "spring",
    stiffness: 100,
    damping: 15,
    mass: 0.8,
  };

  return (
    <div
      onClick={handleClick}
      className="w-full flex flex-col items-center p-[24px] bg-[#F7F8FA] rounded-[16px]"
    >
      <div className="w-full flex flex-row items-start justify-between">
        <div
          className="font-eng-semibold text-[#343434] desktop:text-[16px] leading-[140%] mr-[6px]"
          style={{ letterSpacing: "-0.04em" }}
        >
          {title}
        </div>
        <motion.img
          src={"assets/images/chevron-down.png"}
          alt="arrow-down"
          style={{ width: "24px", height: "24px" }}
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={springTransition}
        />
      </div>
      <AnimatePresence mode="wait">
        {description && isOpen && (
          <motion.div
            className="w-full text-start"
            initial={{
              height: 0,
              opacity: 0,
              overflow: "hidden",
              marginTop: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
              overflow: "visible",
              marginTop: isMobile ? 8 : 20,
            }}
            exit={{ height: 0, opacity: 0, overflow: "hidden", marginTop: 0 }}
            transition={{
              ...springTransition,
              height: { duration: 0.25 },
              opacity: { duration: 0.15 },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => (
                  <p
                    className="font-eng-regular text-[#343434] text-[15px] leading-[140%]"
                    {...props}
                  />
                ),
                h1: ({ node, ...props }) => (
                  <h1
                    className="font-eng-bold text-[#001122] text-[24px] leading-[1.3em] mt-3 mb-2"
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className="font-eng-bold text-[#001122] text-[20px] leading-[1.3em] mt-3 mb-2"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="font-eng-bold text-[#001122] text-[15px] leading-[140%] mt-3 mb-2"
                    style={{ letterSpacing: "-0.04em" }}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc ml-5 my-2" {...props} />
                ),
                li: ({ node, ...props }) => (
                  <li
                    className="font-eng-regular text-[#343434] text-[15px] leading-[140%] my-1"
                    {...props}
                  />
                ),
                strong: ({ node, ...props }) => (
                  <strong
                    className="font-eng-semibold text-[#343434] text-[15px] leading-[140%]"
                    style={{ letterSpacing: "-0.04em" }}
                    {...props}
                  />
                ),
                a: ({ node, ...props }) => (
                  <a
                    className="font-eng-regular text-[#0066CC] underline"
                    {...props}
                  />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-[#DDDDDD] pl-4 my-3 italic text-[#555555]"
                    {...props}
                  />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto w-full my-3">
                    <table className="w-full border-collapse" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <thead className="bg-[#F0F0F0]" {...props} />
                ),
                tbody: ({ node, ...props }) => <tbody {...props} />,
                tr: ({ node, ...props }) => (
                  <tr className="border-b border-[#DDDDDD]" {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="font-eng-bold text-[#343434] p-2 text-left"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td
                    className="font-eng-regular text-[#343434] p-2"
                    {...props}
                  />
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
